import React, { useEffect, useRef, useState } from 'react';

interface Props<T> {
  id?: string;
  value: T | null;
  onChange: (item: T | null) => void;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getSublabel?: (item: T) => string;
  /** Bootstrap input-group addon icon, e.g. 'fa-user'. Rendered to the left of the input. */
  icon?: string;
  placeholder?: string;
  // Static mode: pass items and the component filters internally.
  items?: T[];
  // Async mode: pass pre-filtered suggestions and receive query changes.
  suggestions?: T[];
  onQueryChange?: (query: string) => void;
  // When provided, committing a non-empty query that matches no item (Enter or blur)
  // fires this with the raw text — for fields that accept new free-typed values.
  onFreeEntry?: (value: string) => void;
  disabled?: boolean;
  testId?: string;
}

// Single-value typeahead. Same dropdown styling as MsComboBox but without chips —
// the chosen value displays as plain text in the input. Optional input-group icon addon.
export function SingleSelectComboBox<T>({
  id,
  value,
  onChange,
  getKey,
  getLabel,
  getSublabel,
  icon,
  placeholder = '',
  items,
  suggestions,
  onQueryChange,
  onFreeEntry,
  disabled = false,
  testId,
}: Props<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Neither timer must outlive the component — a late close or query emit after unmount
  // is a state update on a dead component.
  useEffect(() => () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const selectedLabel = value ? getLabel(value) : '';

  // Static mode: filter items internally. Async mode: use suggestions as-is.
  const pool = items !== undefined
    ? (query ? items.filter(i => getLabel(i).toLowerCase().includes(query.toLowerCase())) : items)
    : (suggestions ?? []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const emitQuery = (q: string) => {
    if (!onQueryChange) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onQueryChange(q), 300);
  };

  const select = (item: T) => {
    // The blur's delayed commit sees the stale typed query and would free-enter it over the pick.
    inputRef.current?.blur();
    cancelPendingClose();
    onChange(item);
    setQuery('');
    setEditing(false);
    setOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (onQueryChange) onQueryChange('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    setEditing(true);
    setOpen(true);
    emitQuery(q);
  };

  const handleFocus = () => {
    // Selecting an option blurs the input, which schedules a close 150ms out. Re-focusing
    // inside that window (clicking an option then immediately reopening) must cancel it,
    // or the stale timer closes the dropdown the user just reopened.
    cancelPendingClose();
    setFocused(true);
    setEditing(true);
    setQuery('');
    setOpen(true);
  };

  const commitFreeEntryOrReset = () => {
    const trimmed = query.trim();
    if (onFreeEntry && trimmed && trimmed !== selectedLabel) {
      onFreeEntry(trimmed);
    }
    setQuery('');
    setEditing(false);
  };

  const cancelPendingClose = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // Delay so a dropdown mousedown registers as a select before blur tears it down.
    cancelPendingClose();
    blurTimerRef.current = setTimeout(() => {
      blurTimerRef.current = null;
      setOpen(false);
      commitFreeEntryOrReset();
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = query.trim();
      const exact = pool.find(i => getLabel(i).toLowerCase() === trimmed.toLowerCase());
      if (exact) { select(exact); return; }
      if (pool.length === 1 && trimmed) { select(pool[0]); return; }
      if (onFreeEntry && trimmed) { onFreeEntry(trimmed); setQuery(''); setEditing(false); setOpen(false); }
    } else if (e.key === 'Escape') {
      setQuery('');
      setEditing(false);
      setOpen(false);
    }
  };

  // While editing show the live query; otherwise show the committed selection.
  const inputValue = editing ? query : selectedLabel;
  // When editing with an existing value, surface it as the placeholder so it's not lost.
  const effectivePlaceholder = editing && selectedLabel ? selectedLabel : placeholder;

  const input = (
    <input
      ref={inputRef}
      id={icon ? undefined : id}
      type="text"
      className="form-control"
      autoComplete="off"
      data-testid="combo-input"
      value={inputValue}
      placeholder={effectivePlaceholder}
      disabled={disabled}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );

  return (
    <div
      className={`iio-single-combo${focused ? ' iio-single-combo-focus' : ''}`}
      id={icon ? id : undefined}
      data-testid={testId}
      style={{ position: 'relative' }}
    >
      {icon ? (
        <div className="input-group">
          <span className="input-group-addon"><i className={`fas ${icon}`} aria-hidden="true" /></span>
          {input}
        </div>
      ) : input}

      {open && pool.length > 0 && (
        <div className="ms-res-ctn dropdown-menu" style={{ display: 'block', maxHeight: 200 }}>
          {pool.map((item, i) => {
            const sub = getSublabel?.(item);
            return (
              <div
                key={getKey(item)}
                className={`ms-res-item${i % 2 === 1 ? ' ms-res-odd' : ''}`}
                data-testid="combo-option-item"
                onMouseDown={e => { e.preventDefault(); select(item); }}
              >
                {getLabel(item)}
                {sub && <span className="text-muted"> ({sub})</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
